/* Bullfrogs theme scripts. Every feature degrades to plain HTML forms and links without JavaScript. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* ---------- Header dropdowns and mobile menu ---------- */

  const closeDetails = (details, returnFocus) => {
    details.removeAttribute('open');
    if (returnFocus) details.querySelector('summary')?.focus();
  };

  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-dropdown][open], details[data-menu-drawer][open]').forEach((details) => {
      if (!details.contains(event.target)) closeDetails(details);
    });

    // In-page links (e.g. /#support) don't reload the page, so close the drawer ourselves.
    const drawerLink = event.target.closest('[data-menu-drawer] a[href]');
    if (drawerLink) closeDetails(drawerLink.closest('[data-menu-drawer]'));

    if (event.target.closest('[data-cart-notification-close]') || !event.target.closest('[data-cart-notification]')) {
      hideCartNotification();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-dropdown][open], details[data-menu-drawer][open]').forEach((details) => {
      closeDetails(details, details.contains(document.activeElement));
    });
    hideCartNotification();
  });

  // `toggle` doesn't bubble, so listen during the capture phase.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.matches('[data-menu-drawer]')) {
        document.documentElement.classList.toggle('menu-open', details.open);
      }
      if (details.open && details.matches('[data-dropdown]')) {
        document.querySelectorAll('details[data-dropdown][open]').forEach((other) => {
          if (other !== details && !other.contains(details)) other.removeAttribute('open');
        });
      }
    },
    true
  );

  /* ---------- Cart count and "added to cart" notification ---------- */

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-text]').forEach((element) => {
      element.textContent = count;
    });
  };

  const sizedImageUrl = (src, width) => {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const hideCartNotification = () => {
    document.querySelector('[data-cart-notification]')?.classList.remove('is-visible');
  };

  const showCartNotification = (item) => {
    const notification = document.querySelector('[data-cart-notification]');
    if (!notification) return;

    const row = document.createElement('div');
    row.className = 'cart-notification__item';
    if (item.image) {
      const image = document.createElement('img');
      image.src = sizedImageUrl(item.image, 160);
      image.alt = '';
      image.width = 64;
      image.height = 64;
      row.append(image);
    }
    const info = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'cart-notification__item-title';
    title.textContent = item.product_title;
    info.append(title);
    if (!item.product_has_only_default_variant && item.variant_title) {
      const variant = document.createElement('p');
      variant.className = 'cart-notification__item-variant';
      variant.textContent = item.variant_title;
      info.append(variant);
    }
    row.append(info);

    notification.querySelector('[data-cart-notification-product]').replaceChildren(row);
    notification.classList.add('is-visible');
    notification.focus({ preventScroll: true });
  };

  /* ---------- Quantity steppers ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[name="plus"], button[name="minus"]');
        const input = this.querySelector('input');
        if (!button || !input) return;
        event.preventDefault();

        const step = Number(input.step) || 1;
        const min = input.min === '' ? 0 : Number(input.min);
        const max = input.max === '' ? Infinity : Number(input.max);
        const current = Number(input.value) || 0;
        const next = Math.min(max, Math.max(min, button.name === 'plus' ? current + step : current - step));
        if (next === current) return;

        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  define('quantity-input', QuantityInput);

  /* ---------- Product page: variants, gallery, add to cart ---------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      const data = this.querySelector('[data-product-json]');
      try {
        this.variants = data ? JSON.parse(data.textContent).variants || [] : [];
      } catch (error) {
        this.variants = [];
      }
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');

      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-option-index]')) this.onOptionChange();
      });
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-thumb]');
        if (thumb) this.showMedia(thumb.dataset.thumb);
      });
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));

      this.updateOptionAvailability();
    }

    get fieldsets() {
      return Array.from(this.querySelectorAll('[data-option-index]'));
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    currentVariant() {
      const id = this.form?.querySelector('input[name="id"]')?.value;
      return this.variants.find((variant) => String(variant.id) === id);
    }

    onOptionChange() {
      const options = this.selectedOptions();
      this.fieldsets.forEach((fieldset, index) => {
        const label = fieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = options[index] ?? '';
      });
      this.updateOptionAvailability();

      const variant = this.findVariant(options);
      this.updateButton(variant);
      if (!variant) return;

      const idInput = this.form?.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;

      const price = this.querySelector('[data-product-price]');
      if (price && variant.price_html) price.innerHTML = variant.price_html;

      if (variant.media_id) this.showMedia(String(variant.media_id));

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    // Strike through option values that have no available variant given the options chosen before them.
    updateOptionAvailability() {
      const selected = this.selectedOptions();
      this.fieldsets.forEach((fieldset, index) => {
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              variant.options.slice(0, index).every((value, i) => value === selected[i])
          );
          input.closest('.variant-pill')?.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateButton(variant) {
      const button = this.form?.querySelector('[data-add-button]');
      if (!button) return;
      const label = button.querySelector('[data-add-button-text]') || button;
      if (!variant) {
        button.disabled = true;
        label.textContent = strings.unavailable;
      } else if (!variant.available) {
        button.disabled = true;
        label.textContent = strings.soldOut;
      } else {
        button.disabled = false;
        label.textContent = strings.addToCart;
      }
    }

    showMedia(mediaId) {
      const slides = Array.from(this.querySelectorAll('[data-media-id]'));
      if (!slides.some((slide) => slide.dataset.mediaId === mediaId)) return;
      slides.forEach((slide) => {
        const active = slide.dataset.mediaId === mediaId;
        slide.classList.toggle('is-active', active);
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.querySelectorAll('[data-thumb]').forEach((thumb) => {
        const active = thumb.dataset.thumb === mediaId;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    setError(message) {
      const error = this.querySelector('[data-product-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    async onSubmit(event) {
      // "Go to the cart page" setting, or no popup on the page: let the form post normally.
      if (theme.cartType !== 'notification' || !document.querySelector('[data-cart-notification]')) return;
      event.preventDefault();

      const button = this.form.querySelector('[data-add-button]');
      if (button?.getAttribute('aria-busy') === 'true') return;
      this.setError('');
      button?.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;

      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form),
        });
        const item = await response.json();
        if (!response.ok || item.status) {
          throw new Error(item.description || item.message || strings.addError);
        }
        const cart = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        updateCartCount(cart.item_count);
        showCartNotification(item);
      } catch (error) {
        this.setError(error.message || strings.addError);
      } finally {
        button?.removeAttribute('aria-busy');
        const variant = this.currentVariant();
        if (button) button.disabled = variant ? !variant.available : false;
      }
    }
  }
  define('product-info', ProductInfo);

  /* ---------- Cart page: save quantity changes automatically ---------- */

  class CartForm extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('change', (event) => {
        if (!event.target.matches('input[name="updates[]"]')) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.classList.add('is-updating');
          this.querySelector('form')?.submit();
        }, 450);
      });
    }
  }
  define('cart-form', CartForm);

  /* ---------- Collection and search filters ---------- */

  class FacetForm extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const form = this.querySelector('form');
      if (!form) return;
      form.addEventListener('change', (event) => {
        if (event.target.matches('select, input[type="checkbox"]')) this.apply(form);
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply(form);
      });
    }

    apply(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      this.classList.add('is-loading');
      const action = form.getAttribute('action') || window.location.pathname;
      window.location.assign(`${action}?${params.toString()}`);
    }
  }
  define('facet-form', FacetForm);

  /* ---------- Product recommendations ---------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.bound || !this.dataset.url) return;
      this.bound = true;

      const load = () => {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const fresh = html.querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          })
          .catch(() => {});
      };

      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* ---------- Customer addresses ---------- */

  document.addEventListener('submit', (event) => {
    const message = event.target.dataset?.confirm;
    if (message && !window.confirm(message)) event.preventDefault();
  });

  // shopify_common.js loads after this file, so wait for DOMContentLoaded.
  document.addEventListener('DOMContentLoaded', () => {
    const Selector = window.Shopify && window.Shopify.CountryProvinceSelector;
    if (!Selector) return;
    document.querySelectorAll('[data-address-country]').forEach((select) => {
      const id = select.dataset.addressCountry;
      new Selector(`AddressCountry_${id}`, `AddressProvince_${id}`, { hideElement: `AddressProvinceContainer_${id}` });
    });
  });
})();
